import { SEARCH_PARAM_KEYS, SESSION_STORAGE_KEYS, WORKFLOWS } from "@js/constants/constants-imports.es6.js";
import { SessionStorageUtils, UrlUtils } from "@js/utils/utils-imports.es6.js";

/**
 * Helper methods to help us pull information about where to send the user
 * after a successful login attempt from various locations.
 */
export default class PostLoginRedirectUtils {
  /**
   * If "nextUrl" is present in the query string, send the user back to that
   * URL after a successful login attempt.
   *
   * Example login link: https://www.luminishealth.org/en/login?nextUrl=https%3A%2F%2Fmychart.aahs.org%2FMyChart%2FHome
   * Desired redirect link: https://mychart.aahs.org/MyChart/Home
   */
  static getNextUrlFromQueryString() {
    const url = new URL(window.location.href);
    const nextUrl = url.searchParams.get(SEARCH_PARAM_KEYS.NEXT_URL);
    const nextUrlIsValid = UrlUtils.isValidUrl(nextUrl);

    if (!nextUrlIsValid) {
      return null;
    }

    return nextUrl;
  }

  /**
   * If "postloginurl" is present in the query string, the patient was
   * directed here from MyChart.  The query string value shoud be a relative
   * URL.  Send the user to
   *
   * https://{mychart base URL}/{postloginurl}?{pass along all other query string parameters}
   *
   * after a successful login attempt.
   *
   * Example login link: https://www.luminishealth.org/en/login?postloginurl=%2Fs%2FPMRRKKQV4BXDY2UV6RETGP7HPU%2FMessaging
   * Desired redirect link: https://mychart.aahs.org/MyChart/s/PMRRKKQV4BXDY2UV6RETGP7HPU/Messaging
   *
   * To get an active test link:
   * Epic Hyperdrive > Send Patient Message > Login with the link from the received email
   */
  static getPostLoginUrlFromQueryString() {
    const myChartBaseUrl = PostLoginRedirectUtils.#getMyChartBaseUrl();
    if (!myChartBaseUrl) {
      return null;
    }

    const url = new URL(window.location.href);
    let postLoginRelativeUrl = url.searchParams.get(SEARCH_PARAM_KEYS.MYCHART_POST_LOGIN_URL);

    if (!postLoginRelativeUrl) {
      return null;
    }

    if (postLoginRelativeUrl[0] === '/') {
      postLoginRelativeUrl = postLoginRelativeUrl.slice(1);
    }

    const postLoginAbsoluteUrl = `${myChartBaseUrl}/${postLoginRelativeUrl}`;
    const postLoginAbsoluteUrlIsValid = UrlUtils.isValidUrl(postLoginAbsoluteUrl);

    if (!postLoginAbsoluteUrlIsValid) {
      return null;
    }

    // Remove the "postloginurl" query string parameter key because we've already handled it
    url.searchParams.delete(SEARCH_PARAM_KEYS.MYCHART_POST_LOGIN_URL);

    return UrlUtils.copyQueryString(url.href, postLoginAbsoluteUrl);
  }

  /**
   * If "postloginmode" is present in the query string, the patient was
   * directed here from MyChart.  Send the user to
   *
   * https://{mychart base URL}/inside.asp?mode={postloginmode}&{pass along all other query string parameters}
   *
   * after a successful login attempt.
   *
   * Example login link: https://www.luminishealth.org/en/login?postloginmode=proxyinviteconfirm&InviteCode=WP-24bBMNNTRShwrbU7P7WCgNoSaCAYQHhv6VaLzpATiq9UY-3D-24n6AC5pa32YB2EdFtXuwPhKX2-2Fxz2IjWoHf4Gqe691uI-3D
   * Desired redirect link: https://mychart.aahs.org/MyChart/inside.asp?mode=proxyinviteconfirm&InviteCode=WP-24bBMNNTRShwrbU7P7WCgNoSaCAYQHhv6VaLzpATiq9UY-3D-24n6AC5pa32YB2EdFtXuwPhKX2-2Fxz2IjWoHf4Gqe691uI-3D
   *
   * A note for Luminis Health developers:
   * The supported mode values are defined in {MyChart directory}\config\en-US\pageengine.xml (any with a valid "url" property).
   *
   * To get an active test link:
   * MyChart > Friends and Family Access > Invite friends or family > Login with the link from the received email
   *
   * Related Epic documentation:
   *
   * Proxy invites
   * - https://galaxy.epic.com/Redirect.aspx?DocumentID=100038266&PrefDocID=98659 [Support Proxy Invites Through Non-SAML Single Sign-On (SSO)]
   *
   * eCheck-In
   * - https://galaxy.epic.com/Redirect.aspx?DocumentID=100202353&PrefDocID=98676 [Update Single Sign On (SSO) URLs]
   *
   * Happy Together
   * - https://galaxy.epic.com/Redirect.aspx?DocumentID=100030351&PrefDocID=121377 [Additional Setup for Organizations That Use Single Sign-On]
   * - https://galaxy.epic.com/Redirect.aspx?DocumentID=100108694&PrefDocID=121377 [Patient-Initiated Care Everywhere Links for Single-Sign On (SSO) Organizations]
   *
   * Pay as guest
   * - https://galaxy.epic.com/Redirect.aspx?DocumentID=3472638&PrefDocID=98668 [Configure the Pay as Guest Feature for Single Sign-On Organizations]
   */
  static getPostLoginModeUrlFromQueryString() {
    const myChartBaseUrl = PostLoginRedirectUtils.#getMyChartBaseUrl();
    if (!myChartBaseUrl) {
      return null;
    }

    const url = new URL(window.location.href);
    let postLoginMode = url.searchParams.get(SEARCH_PARAM_KEYS.MYCHART_POST_LOGIN_MODE);

    if (!postLoginMode) {
      return null;
    }

    // Replace the "postloginmode" query string parameter key with "mode"
    url.searchParams.delete(SEARCH_PARAM_KEYS.MYCHART_POST_LOGIN_MODE);
    url.searchParams.set(SEARCH_PARAM_KEYS.MYCHART_MODE, postLoginMode);

    return UrlUtils.copyQueryString(url, `${myChartBaseUrl}/inside.asp`);
  }

  /**
   * If the document.referrer is present in the query string and is a same site
   * redirect or a redirect to a MyChart URL, send the user back to that URL
   * after a successful login attempt.
   */
  static getReferrerUrl() {
    const referrerIsValid = UrlUtils.isValidUrl(document.referrer);
    if (!referrerIsValid) {
      return null;
    }

    const approvedReferrer = UrlUtils.isSameSiteOrMyChartUrl(document.referrer);
    if (!approvedReferrer) {
      return null;
    }

    return document.referrer;
  }

  /**
   * If "loginResumeUrl" is present in session storage, send the user back to
   * that URL after a successful login attempt.
   */
  static getLoginResumeUrlFromSessionStorage() {
    const sessionStorageResumeUrl = SessionStorageUtils.getItem(SESSION_STORAGE_KEYS.LOGIN_RESUME_URL);
    const sessionStorageResumeUrlIsValid = UrlUtils.isValidUrl(sessionStorageResumeUrl);

    if (!sessionStorageResumeUrlIsValid) {
      return null;
    }

    SessionStorageUtils.removeItem(SESSION_STORAGE_KEYS.LOGIN_RESUME_URL);
    return sessionStorageResumeUrl;
  }

  /**
   * If "workflow=mychart" is present in the query string, and no other higher
   * levels of the hierarchy apply, send the user to the MyChart home page after
   * a successful login attempt.  In the URL rewrite rule on the MyChart servers
   * that we use to redirect users from MyChart's login page to
   * LuminisHealth.org's login page, we add that query string to let us know
   * that the patient came from MyChart to the LuminisHealth.org login page.
   *
   * Example login link: https://www.luminishealth.org/en/login?workflow=mychart
   * Desired redirect link: https://mychart.aahs.org/MyChart/Home
   */
  static getMyChartHomePageUrl() {
    const url = new URL(window.location.href);
    const workflow = url.searchParams.get(SEARCH_PARAM_KEYS.WORKFLOW);
    const isMyChartWorkflow = workflow ===  WORKFLOWS.MYCHART;

    if (!isMyChartWorkflow) {
      return null;
    }

    const myChartBaseUrl = PostLoginRedirectUtils.#getMyChartBaseUrl();
    if (!myChartBaseUrl) {
      return null;
    }

    return `${myChartBaseUrl}/Home`;
  }

  /**
   * If the element that defines the default post login page is present, send
   * the user back to that URL after a successful login attempt.
   */
  static getDefaultPostLoginPageUrl() {
    const defaultPostLoginPageSelector = '.field--name-field-default-post-login-page a';
    const defaultPostLoginPage = document.querySelector(defaultPostLoginPageSelector)?.href;
    const defaultPostLoginPageIsValid = UrlUtils.isValidUrl(defaultPostLoginPage);

    if (!defaultPostLoginPageIsValid) {
      return null;
    }

    return defaultPostLoginPage;
  }

  /**
   * If the URL has a custom redirect defined, return that, otherwise return the original URL.
   *
   * @param UrlString the URL to check.
   * @returns {*|string} a custom redirect for the URL, if one exists, or the original.
   */
  static getCustomRedirect(UrlString) {
    const redirectMap = drupalSettings?.luminis?.loginRedirectMap;
    if (!redirectMap) {
      // If we don't have a redirect map, we don't need to proceed.
      return UrlString;
    }

    try {
      const urlObject = new URL(UrlString);
      const urlPath = urlObject.pathname;

      const redirectPath = redirectMap[urlPath];
      if (redirectPath) {
        // Change the path of the url to our custom redirect path and update the url string.
        urlObject.pathname = redirectMap[urlPath];
        return urlObject.toString();
      }
      // eslint-disable-next-line no-unused-vars
    } catch(e) {
      // If some issue happens with creating the URL object, just return the original url since we know it's valid.
      return UrlString;
    }

    // default return the original URL string if no redirects exist.
    return UrlString;
  }

  static #getMyChartBaseUrl() {
    const myChartBaseUrl = drupalSettings?.luminis?.oauthMychartUrl;
    const myChartBaseUrlIsValid = UrlUtils.isValidUrl(myChartBaseUrl);

    if (!myChartBaseUrlIsValid) {
      return null;
    }

    return myChartBaseUrl;
  }
}
